import { useCallback, useRef, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Cloud as CloudIcon,
  Upload,
  FileText,
  Trash2,
  Loader2,
  AlertCircle,
  Lock,
  ArrowRight,
  Download,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCloud } from '@/hooks/useCloud';
import { fetchCloudDocumentAsFile, updateCloudThumbnail } from '@/lib/cloudApi';
import { renderPdfThumbnail } from '@/lib/pdfThumbnail';
import { formatBytes } from '@/lib/billing';
import CloudThumbnail from '@/components/CloudThumbnail';

function UpgradeState({ tier }) {
  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
        <Lock className="w-7 h-7 text-indigo-600" />
      </div>
      <h1 className="text-2xl font-semibold text-gray-900">Cloud storage is a paid feature</h1>
      <p className="text-sm text-gray-600 mt-2">
        Access your documents from any device.
        {tier === 'lifetime'
          ? ' Lifetime license holders can add the Cloud & AI add-on for $4.99/mo.'
          : ' Available on Lite (5 GB), Pro (20 GB).'}
      </p>
      <a
        href="/account/billing"
        className="mt-6 inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700"
      >
        {tier === 'lifetime' ? 'Add Cloud & AI' : 'Upgrade plan'}
        <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

function UsageBar({ usage }) {
  if (!usage) return null;
  const { usedBytes = 0, limitBytes = 0 } = usage;
  const pct = limitBytes > 0 ? Math.min(100, Math.round((usedBytes / limitBytes) * 100)) : 0;
  const warning = pct >= 80;
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between text-xs text-gray-600 mb-1">
        <span>{formatBytes(usedBytes)} of {formatBytes(limitBytes)}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 rounded bg-gray-100 overflow-hidden">
        <div
          className={`h-2 ${warning ? 'bg-amber-500' : 'bg-indigo-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {warning && (
        <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          You're approaching your storage cap. Delete files or upgrade for more room.
        </div>
      )}
    </div>
  );
}

export default function Cloud() {
  const navigate = useNavigate();
  const { user, account, loading: authLoading } = useAuth();
  const { hasCloud, documents, usage, loading, error, refresh, upload, remove } = useCloud({
    autoLoad: Boolean(account),
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [opening, setOpening] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const fileInputRef = useRef(null);

  const onUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        if (file.type && !file.type.includes('pdf')) continue;
        await upload(file, { filename: file.name });
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [upload]);

  const onOpen = useCallback(async (doc) => {
    setOpening(doc.id);
    try {
      const file = await fetchCloudDocumentAsFile(doc.id);
      if (!doc.hasThumbnail) {
        // Fire-and-forget backfill for docs uploaded before thumbnail support.
        // Don't await — the user has already navigated by the time this runs.
        (async () => {
          const thumb = await renderPdfThumbnail(file).catch(() => null);
          if (thumb) await updateCloudThumbnail(doc.id, thumb).catch(() => {});
        })();
      }
      // Pass cloudDocId so the Viewer knows this session is cloud-sourced —
      // ⌘S writes back to cloud, the source chip shows Synced, etc.
      navigate('/viewer', { state: { newDocument: true, file, cloudDocId: doc.id } });
    } catch (err) {
      setUploadError(err.message || 'Could not open document.');
    } finally {
      setOpening(null);
    }
  }, [navigate]);

  const onDownload = useCallback(async (doc) => {
    setDownloading(doc.id);
    try {
      const file = await fetchCloudDocumentAsFile(doc.id);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || file.name || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setUploadError(err.message || 'Could not download document.');
    } finally {
      setDownloading(null);
    }
  }, []);

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold">Sign in to use Scrivar Cloud</h1>
        <button
          type="button"
          onClick={() => navigate('/login?next=/cloud')}
          className="mt-4 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (!hasCloud) {
    return <UpgradeState tier={account?.tier || 'free'} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <CloudIcon className="w-6 h-6 text-indigo-600" />
            Cloud
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Every PDF you save here is available across all your signed-in devices.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => onUpload(Array.from(e.target.files || []))}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300"
          >
            {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>) : (<><Upload className="w-4 h-4" /> Upload PDF</>)}
          </button>
        </div>
      </div>

      <div className="mt-6">
        <UsageBar usage={usage} />
        {(error || uploadError) && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2">
            {error
              ? "Couldn't reach your cloud right now — your saved files are safe. Check your connection."
              : uploadError}
            <button type="button" onClick={refresh} className="ml-2 text-amber-700 underline">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <Loader2 className="animate-spin inline mr-2" size={18} /> Loading your cloud…
          </div>
        ) : documents.length === 0 && !error ? (
          <div className="py-16 text-center text-gray-500 border border-dashed border-gray-200 rounded-xl">
            <CloudIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm">No cloud documents yet.</p>
            <p className="text-xs text-gray-400 mt-1">Upload a PDF to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <div key={doc.id} className="border border-gray-200 rounded-xl bg-white p-4 flex flex-col">
                <div className="aspect-[4/3] rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center mb-3">
                  <CloudThumbnail
                    docId={doc.id}
                    hasThumbnail={doc.hasThumbnail}
                    alt={doc.filename}
                    className="w-full h-full object-contain"
                    placeholder={<FileText className="w-10 h-10 text-gray-300" />}
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold truncate" title={doc.filename}>{doc.filename}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {formatBytes(doc.fileSizeBytes)}
                    {doc.pageCount > 0 && ` · ${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'}`}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpen(doc)}
                    disabled={opening === doc.id}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-indigo-700 disabled:bg-indigo-300"
                  >
                    {opening === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => onDownload(doc)}
                    disabled={downloading === doc.id}
                    aria-label={`Download ${doc.filename}`}
                    title="Download a copy"
                    className="rounded-lg border border-gray-200 text-gray-500 px-2.5 py-1.5 text-xs hover:bg-gray-50 hover:text-brand-blue disabled:opacity-50"
                  >
                    {downloading === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete "${doc.filename}"?`)) remove(doc.id);
                    }}
                    aria-label={`Delete ${doc.filename}`}
                    className="rounded-lg border border-gray-200 text-gray-500 px-2.5 py-1.5 text-xs hover:bg-gray-50 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
